import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const topNavLinks = [
    { label: 'Referrals & Contact', to: '/contact#referrals' },
    { label: 'Young Person', to: '/young-person' }
  ];
  const hasTopNavLinks = topNavLinks.length > 0;

  return (
    <>
      {/* Desktop Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#2f4c74] border-b border-[#2f4c74] shadow-sm hidden lg:block">
        {hasTopNavLinks && (
          <div className="border-b border-[#244061] bg-[#2f4c74]">
            <div className="flex items-center justify-end h-10 px-8">
              {/* Top nav links */}
              <div className="flex items-center space-x-6 text-sm text-white">
                {topNavLinks.map(({ label, to }) => (
                  <Link key={label} to={to} className="hover:text-white transition">
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between h-32 px-6">
          {/* Logo in main nav area */}
          <Link to="/" className="flex items-center gap-4">
            <img
              src="/assets/images/logo12.png"
              alt="Testa Care logo"
              className="h-32 w-auto max-w-[20rem] object-contain"
              loading="lazy"
            />
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-white leading-tight">Testa</span>
              <span className="text-2xl font-bold text-white leading-tight">Residential Care</span>
            </div>
          </Link>

          {/* Navigation Links */}
          <nav className="flex items-center space-x-3 text-lg">
            <Link to="/our-homes" className="text-[#e6edf7] hover:text-white transition">
              Our Homes
            </Link>
            <span className="text-[#6b7f9d]">|</span>
            <Link to="/therapy-services" className="text-[#e6edf7] hover:text-white transition">
              Therapy Services
            </Link>
            <span className="text-[#6b7f9d]">|</span>
            <Link to="/our-team" className="text-[#e6edf7] hover:text-white transition">
              Our Team
            </Link>
            <span className="text-[#6b7f9d]">|</span>
            <Link to="/about" className="text-[#e6edf7] hover:text-white transition">
              About Us
            </Link>
            <span className="text-[#6b7f9d]">|</span>
            <Link to="/join-our-team" className="text-[#e6edf7] hover:text-white transition">
              Join our team
            </Link>
          </nav>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#2f4c74] shadow-md lg:hidden">
        <div className="h-32 sm:h-36 flex items-center justify-between px-4">
          {/* Mobile Logo */}
          <Link to="/" className="flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
            <img
              src="/assets/images/logo12.png"
              alt="Testa Care logo"
              className="h-28 sm:h-32 w-auto object-contain"
              loading="lazy"
            />
            <div className="flex flex-col">
              <span className="text-lg sm:text-xl font-bold text-white leading-tight">Testa</span>
              <span className="text-lg sm:text-xl font-bold text-white leading-tight">Residential Care</span>
            </div>
          </Link>

          {/* Mobile Menu Button - Optimized for touch (min 44x44px) */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="relative w-11 h-11 flex items-center justify-center rounded-md hover:bg-[#3d5a82] active:bg-[#4a6790] transition-colors"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            <div className="flex flex-col items-center justify-center w-6 h-5">
              <span
                className={`block w-6 h-0.5 bg-white rounded transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-2' : 'mb-1.5'
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-white rounded transition-all duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : 'mb-1.5'
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-white rounded transition-all duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              />
            </div>
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Slide-out Menu */}
            <div className="fixed top-24 sm:top-28 left-0 right-0 bottom-0 bg-[#1f2c43] z-50 overflow-y-auto animate-slideDown">
              <nav className="flex flex-col py-2">
                <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#9fb0c8] border-b border-[#263752]">
                  Helpful links
                </div>
                {topNavLinks.map(({ label, to }) => (
                  <Link
                    key={label}
                    to={to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-3 text-[#e6edf7] hover:bg-[#24344f] hover:text-white active:bg-[#2a3b54] transition-colors border-b border-[#263752]/50"
                  >
                    {label}
                  </Link>
                ))}
                <div className="px-4 py-3 mt-2 text-xs font-semibold uppercase tracking-wider text-[#9fb0c8] border-b border-[#263752]">
                  Explore
                </div>
                <Link
                  to="/our-homes"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-[#e6edf7] hover:bg-[#24344f] hover:text-white active:bg-[#2a3b54] transition-colors border-b border-[#263752]/50"
                >
                  Our Homes
                </Link>
                <Link
                  to="/therapy-services"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-[#e6edf7] hover:bg-[#24344f] hover:text-white active:bg-[#2a3b54] transition-colors border-b border-[#263752]/50"
                >
                  Therapy Services
                </Link>
                <Link
                  to="/our-team"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-[#e6edf7] hover:bg-[#24344f] hover:text-white active:bg-[#2a3b54] transition-colors border-b border-[#263752]/50"
                >
                  Our Team
                </Link>
                <Link
                  to="/about"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-[#e6edf7] hover:bg-[#24344f] hover:text-white active:bg-[#2a3b54] transition-colors border-b border-[#263752]/50"
                >
                  About Us
                </Link>
                <Link
                  to="/join-our-team"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-[#e6edf7] hover:bg-[#24344f] hover:text-white active:bg-[#2a3b54] transition-colors border-b border-[#263752]/50"
                >
                  Join our team
                </Link>
              </nav>
            </div>
          </>
        )}
      </header>
    </>
  );
};

export default Header;
